/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2020
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import CachedIcon from "@material-ui/icons/Cached";
import GetApp from "@material-ui/icons/GetApp";
import { useTheme } from "@material-ui/core/styles";

import SpotStatusFilter from "../components/filters/SpotStatusFilter";

import SpotServiceLevelMultiFilter from "../components/filters/SpotServiceLevelMultiFilter";
import SpotOriginStateMultiFilter from "../components/filters/SpotOriginStateMultiFilter";
import SpotDestinationStateMultiFilter from "../components/filters/SpotDestinationStateMultiFilter";

import RefreshResetFilters from "../components/filters/RefreshResetFilters";
import DateRangePicker from "../components/date-picker/DateRangePicker";

import { updateSpotFiltersV2 } from "../slices/spotFiltersSlice";

import { fetchServiceLevels } from "../slices/serviceLevelsSlice";
import defaultSpotFilters from "../helpers/defaultSpotFilters";

import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { format, parse } from "date-fns";
import { useHistory } from "react-router-dom";
import {
  isCarrier,
  isShipperManager,
  isShipper,
} from "../helpers/goTrackUserUtils";
import types from "../helpers/types";
import { getSpotLegsCsv } from "../api/api";

const StyledWrapperGridItem = styled(Grid)`
  background-color: ${(props) => props.theme.palette.background.lightGrey};
  flex: 0 0 auto !important;
  padding: 16px;
  .MuiFormControl-marginDense {
    margin-bottom: 0;
    margin-top: 0;
  }
  .MuiAutocomplete-marginDense {
    margin-bottom: 0;
    margin-top: 0;
  }
`;

const StyledButton = styled(Button)`
  padding: 6px !important;
  margin-top: 4px;
  min-width: 32px !important;
  .MuiButton-startIcon {
    margin: 0;
  }
`;

const StyledFilterGridItem = styled(Grid)`
  padding-right: 0 !important;
`;

const StyledFilterGridAutocompleteItem = styled(Grid)`
padding-right: 0 !important;
`;

const SpotFiltersContainer = ({ handleRefresh }) => {
  //using local state and redux state here to keep the date picker in sync,
  //and to only update the URL params when the full date range is selected
  const filters = useSelector((state) => state.spotFilters);
  const user = useSelector((state) => state.goTrackUser.goTrackUser);

  const { departureDate, arrivalDate } = filters;
  const [startDateLocal, setStartDateLocal] = useState(null);
  const [endDateLocal, setEndDateLocal] = useState(null);
  const [lastUpdateDate, setLastUpdateDate] = useState(new Date());

  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const handleRefreshLocal = () => {
    setLastUpdateDate(new Date());
    handleRefresh();
  };

  useEffect(() => {
    if (departureDate && arrivalDate) {
      setStartDateLocal(parse(departureDate, "MM/dd/yyyy", new Date()));
      setEndDateLocal(parse(arrivalDate, "MM/dd/yyyy", new Date()));
    }
  }, [departureDate, arrivalDate]);

  useEffect(() => {
    dispatch(fetchServiceLevels());
  }, [dispatch]);

  useEffect(() => {
    setLastUpdateDate(new Date());
  }, [filters]);

  const handleDateSelect = (startDate, endDate) => {
    setStartDateLocal(startDate);
    setEndDateLocal(endDate);
    if (startDate && endDate) {
      dispatch(
        updateSpotFiltersV2({
          filters: {
            departureDate: startDate
              ? format(startDate, "MM/dd/yyyy")
              : startDateLocal,
            arrivalDate: endDate ? format(endDate, "MM/dd/yyyy") : endDateLocal,
          },
          history,
        })
      );
    } else if (!startDate && !endDate) {
      dispatch(
        updateSpotFiltersV2({
          filters: {
            departureDate: null,
            arrivalDate: null,
          },
          history,
        })
      );
    }
  };

  const handleResetFilters = () => {
    dispatch(
      updateSpotFiltersV2({
        filters: {
          ...defaultSpotFilters,
          status: filters.status,
        },
        history,
      })
    );
  };

  const triggerFileDownload = async () => {
    await getSpotLegsCsv(filters.departureDate, filters.arrivalDate).then(
      (response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        const filename = response.headers["content-disposition"]
          .split(";")
          .find((name) => name.includes("filename="))
          .replace("filename=", "")
          .replaceAll('"', "")
          .trim();
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
      }
    );
  };

  const statusTypePosted =
    types.spotLegStatusWithCreationType.find(
      (obj) => obj.id === parseInt(filters.status, 10)
    )?.status === types.spotLegStatusEnum.posted;

  const userCanDownloadSpotLegs =
    isCarrier(user.roles) ||
    (isShipperManager(user.roles) && statusTypePosted) ||
    (isShipper(user.roles) && statusTypePosted);

  return (
    <StyledWrapperGridItem item theme={theme} xs={12}>
      {userCanDownloadSpotLegs && (
        <StyledFilterGridItem
          item
          style={{
            textAlign: "right",
            position: "absolute",
            right: 12,
            paddingTop: 10,
          }}
        >
          <Button
            variant="contained"
            color="default"
            style={{ minWidth: 0 }}
            onClick={triggerFileDownload}
            size="small"
            data-testid="export-spotlegs-button"
          >
            <GetApp />
          </Button>
        </StyledFilterGridItem>
      )}
      <Grid container alignItems="center" spacing={2}>
        <StyledFilterGridItem item>
          <SpotStatusFilter />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <DateRangePicker
            placeholder="Sched Depart Date"
            handleDateSelect={handleDateSelect}
            startDate={startDateLocal}
            endDate={endDateLocal}
            inputLabel="Sched Depart Date"
            inputLabelWidth={120}
          />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <SpotServiceLevelMultiFilter />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <SpotOriginStateMultiFilter />
        </StyledFilterGridItem>
        <StyledFilterGridItem item>
          <SpotDestinationStateMultiFilter />
        </StyledFilterGridItem>
        <StyledFilterGridItem item xs={3}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <StyledButton
                variant="contained"
                onClick={handleRefreshLocal}
                startIcon={<CachedIcon style={{ marginRight: "0px" }} />}
              ></StyledButton>
            </Grid>
            <Grid item>
              <RefreshResetFilters
                handleReset={handleResetFilters}
                lastUpdateDate={lastUpdateDate}
              />
            </Grid>
          </Grid>
        </StyledFilterGridItem>
      </Grid>
    </StyledWrapperGridItem>
  );
};

export default SpotFiltersContainer;
